import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { ChatDemo } from '../../components/ia-landing/ChatDemo';
import { CopyUrl } from '../../components/ia-landing/CopyUrl';
import { Connections } from '../../components/ia-landing/Connections';
import type { IaLandingProps } from '../../types/ia-landing';

const PROMPTS = [
  { icon: '🥑', text: '¿A cuánto tengo la palta hass esta semana?' },
  { icon: '🔁', text: 'Repetí mi último pedido, pero sin cebolla y para el viernes.' },
  { icon: '🍝', text: 'Armame un pedido para una caprese de 40 cubiertos.' },
  { icon: '🧾', text: 'Mandame la factura del pedido de ayer.' },
  { icon: '💳', text: '¿Cuánto debo en la cuenta corriente y qué tengo pendiente?' },
  { icon: '✋', text: 'Cancelá el pedido que hice recién.' },
];

const PROMISES = [
  { title: 'Ve sólo lo tuyo', text: 'Tus precios, tus pedidos, tus facturas y tu cuenta corriente. Nada de otros clientes.' },
  { title: 'Nunca compra sin tu OK', text: 'Primero te muestra el pedido armado con el total, la fecha y el medio de pago. Recién cuando le decís que sí, lo hace.' },
  { title: 'Sin compartir tu contraseña', text: 'Ingresás en nuestra página, como siempre, y autorizás al asistente. Él nunca ve tu clave.' },
  { title: 'Lo desconectás cuando quieras', text: 'Desde esta misma página, en «Tus conexiones». Deja de tener acceso al instante.' },
];

const FAQ = [
  { q: '¿Tiene costo?', a: 'No. Es parte de tu cuenta de Abasto Virtual. Lo único que necesitás es tu asistente (Claude o ChatGPT).' },
  { q: '¿Qué asistentes funcionan?', a: 'Claude (web, escritorio y celular), ChatGPT y cualquier otro que acepte conectores MCP con inicio de sesión (OAuth), como Claude Code.' },
  { q: '¿La IA puede confirmar un pedido sin mi autorización?', a: 'No. Primero arma el pedido y te muestra el resumen con los productos, el total, la fecha y el medio de pago. Lo confirma recién cuando le decís que sí; si algo no te cierra, le pedís que lo cambie.' },
  { q: '¿Los precios son los míos?', a: 'Sí, los mismos que ves en la web con tu cuenta, con tu lista de precios. Los descuentos y beneficios que tengas se aplican al confirmar.' },
  { q: 'Compro para una organización con varias sucursales, ¿cómo funciona?', a: 'Igual que en la web: ves y pedís para las sucursales que tenés asignadas, con sus medios de pago.' },
];

export default function IaLanding({ appUrl, mcpUrl, claudeInstallUrl, loginUrl, connections }: IaLandingProps) {
  return (
    <MainLayout>
      <Head>
        <title>Protocolo Verdulero: pedile la verdura a tu IA | Abasto Virtual</title>
        <meta
          name="description"
          content="Conectá Claude o ChatGPT a tu cuenta de Abasto Virtual: consultá tus precios, armá pedidos, seguí tus compras, facturas y cuenta corriente, todo hablando."
        />
        <link rel="canonical" href={`${appUrl}/ia`} />
      </Head>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0b1a0c] text-white">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#1EAA25]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-48 right-0 h-[520px] w-[520px] rounded-full bg-orange-500/20 blur-3xl" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:py-28">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm font-semibold tracking-wide text-[#7ee07f]">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#7ee07f]" />
              Protocolo Verdulero
            </span>
            <h1 className="mt-6 text-4xl font-black leading-tight sm:text-6xl">
              Pedile la verdura{' '}
              <span className="bg-gradient-to-r from-[#7ee07f] via-yellow-300 to-orange-400 bg-clip-text text-transparent">a tu IA.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/80">
              Conectá Claude o ChatGPT a tu cuenta de Abasto Virtual y hacé las compras hablando: precios, pedidos, facturas y cuenta corriente, en una conversación.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={claudeInstallUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-[#1EAA25] px-6 py-4 text-base font-bold text-white shadow-lg shadow-[#1EAA25]/30 transition-transform hover:-translate-y-0.5"
              >
                Conectar con Claude
                <span aria-hidden>→</span>
              </a>
              <a
                href="#conectar"
                className="inline-flex items-center rounded-xl border border-white/25 px-6 py-4 text-base font-bold text-white transition-colors hover:bg-white/10"
              >
                Usar con ChatGPT u otro
              </a>
            </div>
            <p className="mt-4 text-sm text-white/50">Un clic, ingresás con tu cuenta y listo.</p>
          </div>

          <div className="flex justify-center lg:justify-end">
            <ChatDemo />
          </div>
        </div>
      </section>

      {/* What to ask */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-3xl font-extrabold text-gray-900">Pedíselo como se lo pedirías a tu verdulero</h2>
          <p className="mt-3 max-w-2xl text-gray-600">Sin menús ni formularios. Algunas cosas que le podés decir:</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROMPTS.map((prompt) => (
              <div
                key={prompt.text}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#1EAA25]/40 hover:shadow-md"
              >
                <span className="text-2xl" aria-hidden>{prompt.icon}</span>
                <p className="mt-3 font-medium text-gray-800">“{prompt.text}”</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to connect */}
      <section id="conectar" className="bg-gray-900 py-16 text-white">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-3xl font-extrabold">Conectalo en un minuto</h2>
          <p className="mt-3 max-w-2xl text-white/70">Esta es la dirección de tu conector. La vas a necesitar si no usás el botón de Claude:</p>
          <div className="mt-6 max-w-2xl">
            <CopyUrl url={mcpUrl} />
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            <div className="min-w-0 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <h3 className="text-xl font-bold">Claude</h3>
              <ol className="mt-4 list-decimal space-y-2 pl-5 text-white/80">
                <li>Tocá <b>Conectar con Claude</b>: se abre Claude con el conector ya cargado.</li>
                <li>Confirmá e ingresá con tu cuenta de Abasto Virtual.</li>
                <li>Autorizá la conexión. Ya podés pedirle lo que necesites.</li>
              </ol>
              <a
                href={claudeInstallUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex rounded-lg bg-[#1EAA25] px-4 py-2 font-semibold text-white hover:opacity-90"
              >
                Conectar con Claude
              </a>
              <p className="mt-3 text-sm text-white/50">
                A mano: en Claude, Configuración › Conectores › Agregar conector personalizado, y pegá la dirección.
              </p>
            </div>

            <div className="min-w-0 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <h3 className="text-xl font-bold">ChatGPT</h3>
              <ol className="mt-4 list-decimal space-y-2 pl-5 text-white/80">
                <li>En chatgpt.com, entrá a <b>Complementos</b> en el menú de la izquierda.</li>
                <li>Tocá <b>Añadir</b> › <b>Añadir servidor MCP personalizado</b>.</li>
                <li>Poné de nombre <b>Abasto Virtual</b>, pegá la dirección de arriba en <b>Conexión</b> y dejá <b>OAuth</b> como autenticación.</li>
                <li>Marcá <b>Lo entiendo y quiero continuar</b> y tocá <b>Crear como complemento</b>.</li>
                <li>Ingresá con tu cuenta de Abasto Virtual y autorizá la conexión.</li>
              </ol>
            </div>

            <div className="min-w-0 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <h3 className="text-xl font-bold">Claude Code y otros</h3>
              <p className="mt-4 text-white/80">Cualquier asistente que acepte conectores MCP con inicio de sesión. En Claude Code:</p>
              <pre className="mt-4 whitespace-pre-wrap break-all rounded-lg bg-black/50 p-3 font-mono text-xs text-[#7ee07f]">
                claude mcp add --transport http abastovirtual {mcpUrl}
              </pre>
              <p className="mt-3 text-sm text-white/50">La primera vez te pide ingresar con tu cuenta.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-3xl font-extrabold text-gray-900">Tu cuenta, tus reglas</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {PROMISES.map((promise) => (
              <div key={promise.title} className="flex gap-4">
                <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 font-bold text-green-800" aria-hidden>✓</span>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{promise.title}</h3>
                  <p className="mt-1 text-gray-600">{promise.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Connections connections={connections} loginUrl={loginUrl} />

      {/* FAQ */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-3xl font-extrabold text-gray-900">Preguntas frecuentes</h2>
          <div className="mt-8 divide-y divide-gray-200 rounded-2xl border border-gray-200">
            {FAQ.map((item) => (
              <details key={item.q} className="group px-6 py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between font-semibold text-gray-900">
                  {item.q}
                  <span className="text-gray-400 transition-transform group-open:rotate-45" aria-hidden>+</span>
                </summary>
                <p className="mt-3 text-gray-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
